import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Loader2, Minus, Snowflake } from "lucide-react";
import { fetchProducts } from "../../lib/api";
import { useCart } from "../../context/CartContext";
import { toast } from "sonner";

const PT_MONTHS = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

const fmtDate = (iso) => {
    if (!iso) return null;
    try {
        const d = new Date(iso);
        return `${d.getDate()} ${PT_MONTHS[d.getMonth()]}`;
    } catch {
        return null;
    }
};

export const Products = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const { addItem, items, updateQty } = useCart();

    const qtyOf = (id) => items.find((i) => i.id === id)?.quantity || 0;

    useEffect(() => {
        fetchProducts()
            .then(setProducts)
            .catch((e) => {
                console.error("products fetch failed", e);
                toast.error("Não foi possível carregar os sabores");
            })
            .finally(() => setLoading(false));
    }, []);

    const handleAdd = (p) => {
        addItem(p, false);
        toast.success(`${p.name} adicionado ao carrinho`);
    };

    return (
        <section
            id="produtos"
            data-testid="products-section"
            className="py-20 md:py-28 bg-brand-cream relative overflow-hidden"
        >
            <div
                aria-hidden
                className="absolute inset-0 opacity-[0.04] bg-hex-pattern pointer-events-none"
                style={{ backgroundImage: "url('/brand/pattern-2.png')" }}
            />
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                    <div className="max-w-2xl">
                        <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-orange">
                            A Coleção
                        </span>
                        <h2 className="mt-3 font-display font-medium text-4xl md:text-5xl text-brand-ink leading-tight">
                            Oito sabores. <span className="italic text-brand-orange">Um só compromisso.</span>
                        </h2>
                        <p className="mt-4 text-lg text-brand-ink-soft">
                            Fruta colhida no auge, servida no momento. Sem conservantes, sem pressa.
                        </p>
                    </div>
                    <div className="hidden md:flex items-center gap-2 text-sm text-brand-ink-soft">
                        <span className="h-2 w-2 rounded-full bg-brand-green animate-pulse" />
                        Disponível para entrega
                    </div>
                </div>

                {loading ? (
                    <div
                        className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6"
                        data-testid="products-skeleton"
                    >
                        {Array.from({ length: 8 }).map((_, i) => (
                            <div
                                key={i}
                                className="bg-white rounded-2xl overflow-hidden shadow-soft animate-pulse"
                            >
                                <div className="aspect-square bg-brand-yellow/60" />
                                <div className="p-5 space-y-3">
                                    <div className="h-5 w-2/3 rounded-full bg-brand-yellow/60" />
                                    <div className="h-3 w-full rounded-full bg-brand-yellow/40" />
                                    <div className="flex items-center justify-between pt-2">
                                        <div className="h-6 w-16 rounded-full bg-brand-yellow/60" />
                                        <div className="h-10 w-10 rounded-full bg-brand-yellow/60" />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                        {products.map((p) => (
                            <article
                                key={p.id}
                                data-testid={`product-card-${p.id}`}
                                className="group bg-white rounded-2xl overflow-hidden shadow-soft hover:shadow-pop transition-all duration-300 hover:-translate-y-2 flex flex-col"
                            >
                                <Link
                                    to={`/sabores/${p.id}`}
                                    data-testid={`product-link-${p.id}`}
                                    className="relative aspect-square overflow-hidden block"
                                    style={{ background: p.color + "33" }}
                                >
                                    <img
                                        src={p.image}
                                        alt={p.name}
                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                        loading="lazy"
                                    />
                                    <div
                                        className="absolute top-3 left-3 h-2 w-8 rounded-full"
                                        style={{ background: p.color }}
                                    />
                                    {p.badge && (
                                        <div
                                            className="absolute top-3 right-3 rounded-full bg-brand-orange text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 shadow-soft"
                                            data-testid={`product-badge-${p.id}`}
                                        >
                                            {p.badge}
                                        </div>
                                    )}
                                    {p.harvest_date && (
                                        <div
                                            className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-white/95 backdrop-blur px-2.5 py-1 shadow-soft"
                                            data-testid={`freshness-seal-${p.id}`}
                                        >
                                            <Snowflake className="h-3 w-3 text-brand-green" />
                                            <span className="text-[10px] font-bold uppercase tracking-wider text-brand-ink">
                                                Colhido {fmtDate(p.harvest_date)}
                                            </span>
                                        </div>
                                    )}
                                </Link>
                                <div className="p-5 flex-1 flex flex-col">
                                    <Link
                                        to={`/sabores/${p.id}`}
                                        className="hover:text-brand-orange transition-colors"
                                    >
                                        <h3 className="font-display font-medium text-xl text-brand-ink">
                                            {p.name}
                                        </h3>
                                    </Link>
                                    <p className="mt-1 text-sm text-brand-ink-soft line-clamp-2 flex-1">
                                        {p.description}
                                    </p>
                                    <div className="mt-4 flex items-center justify-between">
                                        <div className="font-display font-medium text-2xl text-brand-ink">
                                            {p.price.toFixed(2).replace(".", ",")} €
                                        </div>
                                        {qtyOf(p.id) > 0 ? (
                                            <div
                                                className="inline-flex items-center bg-brand-green-soft rounded-full p-1"
                                                data-testid={`card-stepper-${p.id}`}
                                            >
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        updateQty(p.id, qtyOf(p.id) - 1)
                                                    }
                                                    data-testid={`card-decrease-${p.id}`}
                                                    className="h-8 w-8 rounded-full bg-white text-brand-ink hover:bg-brand-cream flex items-center justify-center shadow-soft transition-colors"
                                                    aria-label={`Remover um ${p.name}`}
                                                >
                                                    <Minus className="h-3.5 w-3.5" />
                                                </button>
                                                <span
                                                    className="px-2.5 text-sm font-bold text-brand-ink min-w-[2ch] text-center"
                                                    data-testid={`card-qty-${p.id}`}
                                                >
                                                    {qtyOf(p.id)}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => handleAdd(p)}
                                                    data-testid={`add-to-cart-${p.id}`}
                                                    className="h-8 w-8 rounded-full bg-brand-green hover:bg-brand-green-dark text-white flex items-center justify-center shadow-soft transition-colors"
                                                    aria-label={`Adicionar ${p.name} ao carrinho`}
                                                >
                                                    <Plus className="h-3.5 w-3.5" />
                                                </button>
                                            </div>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={() => handleAdd(p)}
                                                data-testid={`add-to-cart-${p.id}`}
                                                className="h-10 w-10 rounded-full bg-brand-green hover:bg-brand-green-dark text-white flex items-center justify-center transition-all hover:scale-110 shadow-soft"
                                                aria-label={`Adicionar ${p.name} ao carrinho`}
                                            >
                                                <Plus className="h-5 w-5" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
};
